import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import {Product} from "./product-details/interface/product";
import {Material} from "./product-details/interface/material";
import {Color} from "./product-details/interface/color";
import {Pattern} from "./product-details/interface/pattern";
import {StoryDetails} from "./story-details/interface/story-details/story-details";
import {BlogContentDetails} from "./custom-content/interface/blog-content-details";
import {FilterProductPreview} from "./filter/interface/filter-product-preview";
import {BlogDetails} from "./blog/interface/blog-details";
import {StoryDetailsV2} from "./stories/interface/story-details";

@Injectable({
	providedIn: 'root'
})
export class AddStructuredDataService {

	private renderer: Renderer2;

	constructor(rendererFactory: RendererFactory2) {
		this.renderer = rendererFactory.createRenderer(null, null);
	}

	private _getProductUrl(productGroup: string, slug: string): string {
		return productGroup + '-product/' + slug;
	}

	private _getMaterialCSV(materials: Material[]): string {
		return materials.map(material => material.name).join();
	}

	private _getColorCSV(colors: Color[]): string {
		return colors.map(color => color.name).join();
	}

	private _getPatternCSV(patterns: Pattern[] | undefined): string {
		return patterns ? patterns.map(pattern => pattern.name).join() : '';
	}

	private _insertSchema(schema: Object): void {
		const script = this.renderer.createElement('script');
		this.renderer.setAttribute(script, 'type', 'application/ld+json');
		const schemaJson = JSON.stringify(schema);
		const text = this.renderer.createText(schemaJson);
		this.renderer.appendChild(script, text);
		this.renderer.appendChild(document.head, script);
	}

	public mapToProductStructure(product: Product): void {
		let productSchema: Object =  {
			"@context": "http://schema.org/",
			"@type": "Product",
			"name": product.name,
			"description": this.removeHtmlTags(product.productOverview),
			"sku": product.sku,
			"category": product.category.name,
			"brand": {
				"@type": "Brand",
				"name": "Anuprerna"
			},
			"image": product.heroImage,
			"url": "https://anuprerna.com/product/" + this._getProductUrl(product.productGroup, product.slug),
			"offers": {
				"@type": "Offer",
				"url": "https://anuprerna.com/product/" + this._getProductUrl(product.productGroup, product.slug),
				"priceCurrency": "INR",
				"price": product.price.toString(),
				"itemCondition": "http://schema.org/NewCondition",
				"availability": "http://schema.org/InStock"
			},
			"additionalType": "http://schema.org/Textile",
			"material": this._getMaterialCSV(product.materials),
			"color": this._getColorCSV(product.colors),
			"additionalProperty": [
				{
					"@type": "PropertyValue",
					"name": "Segment Category",
					"value": product.segment.name
				},
				{
					"@type": "PropertyValue",
					"name": "Sub Category",
					"value": product.subCategory.name
				},
				{
					"@type": "PropertyValue",
					"name": "Pattern",
					"value": this._getPatternCSV(product.patterns),
				}
			],
			"aggregateRating": {
				"@type": "AggregateRating",
				"ratingValue": "4.8",
				"reviewCount": "219"
			},
			"mainEntityOfPage": {
				"@type": "WebPage",
				"@id": "https://anuprerna.com/product/" + this._getProductUrl(product.productGroup, product.slug)
			}
		}

		this._insertSchema(productSchema);
	}

	public mapToCarouselStructure(products: FilterProductPreview[], productGroup: string): void {

		let productlist: Object[] = products.map((product, i) => {
			return {
				"@type": "ListItem",
				"position": i + 1,
				"item": {
					"@type": "Product",
					"name": product.name,
					"image": product.hero_image,
					"url": "https://anuprerna.com/product/" + this._getProductUrl(productGroup, product.slug),
					"sku": product.sku,
					"offers": {
						"@type": "Offer",
						"priceCurrency": "INR",
						"price": product.price.toString(),
						"availability": "https://schema.org/InStock"
					},
					"aggregateRating": {
						"@type": "AggregateRating",
						"ratingValue": "4.8",
						"reviewCount": "219"
					},
				}
			}
		});
		let productSchema: Object = {
			"@context": "https://schema.org",
			"@type": "ItemList",
			"itemListElement": [
				...productlist
			]
		}

		this._insertSchema(productSchema);
	}

	public mapToProductMerchantListingStructure(product: Product): void {
		let merchantSchema: Object =  {
			"@context": "https://schema.org/",
			"@type": "Product",
			"name": product.name,
			"image": [
				product.heroImage,
				product.hoverImage,
				...product.imageGallerySEOList.map(image => image.image)
			],
			"description": this.removeHtmlTags(product.productOverview),
			"sku": product.sku,
			"category": product.category.name,
			"brand": {
				"@type": "Brand",
				"name": "Anuprerna"
			},
			// "review": {
			// 	"@type": "Review",
			// 	"reviewRating": {
			// 		"@type": "Rating",
			// 		"ratingValue": 4,
			// 		"bestRating": 5
			// 	},
			// 	"author": {
			// 		"@type": "Person",
			// 		"name": "Fred Benson"
			// 	}
			// },
			"aggregateRating": {
				"@type": "AggregateRating",
				"ratingValue": "4.8",
				"reviewCount": "219"
			},
			"offers": {
				"@type": "Offer",
				"url": "https://anuprerna.com/product/" + this._getProductUrl(product.productGroup, product.slug),
				"priceCurrency": "INR",
				"price": product.price.toString(),
				"itemCondition": "https://schema.org/UsedCondition",
				"availability": "https://schema.org/InStock"
			}
		};

		this._insertSchema(merchantSchema);
	}

	public mapStoryToArticleStructure(article: StoryDetails | StoryDetailsV2): void {
		let articleSchema: Object =  {
			"@context": "https://schema.org",
			"@type": "BlogPosting",
			"headline": article.title,
			"image": [
				article.bannerImageDesktop,
				article.bannerImageMobile,
			],
			"datePublished": new Date(article.timeOfCreation),
			"dateModified": new Date(article.lastUpdateTime),
			"author": [{
				"@type": "Organization",
				"name": "Anuprerna",
				"url": "https://anuprerna.com/"
			}],
			"publisher":
				{
					"@type": "Organization",
					"name": "Anuprerna",
					"url": "https://anuprerna.com/"
				},
		}
		this._insertSchema(articleSchema);
	}

	public mapCustomContentToArticleStructure(article: BlogContentDetails): void {
		let articleSchema: Object =  {
			"@context": "https://schema.org",
			"@type": "BlogPosting",
			"headline": article.title,
			"image": [
				article.bannerImageDesktop,
				article.bannerImageMobile,
			],
			"datePublished": new Date(article.timeOfCreation),
			"dateModified": new Date(article.lastUpdateTime),
			"author": [{
				"@type": "Organization",
				"name": "Anuprerna",
				"url": "https://anuprerna.com/"
			}],
			"publisher":
				{
					"@type": "Organization",
					"name": "Anuprerna",
					"url": "https://anuprerna.com/"
				},
		}
		this._insertSchema(articleSchema);
	}

	public mapBlogToArticleStructure(article: BlogDetails): void {
		let articleSchema: Object =  {
			"@context": "https://schema.org",
			"@type": "BlogPosting",
			"headline": article.title,
			"image": [
				article.bannerImageDesktop,
				article.bannerImageMobile,
			],
			"datePublished": new Date(article.timeOfCreation),
			"dateModified": new Date(article.lastUpdateTime),
			"author": [{
				"@type": "Organization",
				"name": "Anuprerna",
				"url": "https://anuprerna.com/"
			}],
			"publisher":
				{
					"@type": "Organization",
					"name": "Anuprerna",
					"url": "https://anuprerna.com/"
				},
		}
		this._insertSchema(articleSchema);
	}

	public removeHtmlTags(str: string): string {
		return str.replace(/<\/?[^>]+(>|$)/g, "");
	}
}
